import React, {ReactNode, useEffect, useRef} from 'react'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination, Mousewheel, Keyboard} from 'swiper/modules'
import type SwiperType from 'swiper'
import {useHeaderSize, useViewport} from '_customHooks'
// import {useHeaderDispatch} from '_context'

interface AnimatedSlideProps {
    data: {
        title_animation_type: 'no-animation' | 'fixed' | 'size' | 'attached' | 'fade' | 'carousel'
        module_attached?: boolean
        svg?: {url: string; metadata: {height: number; width: number}}
        title?: string
        cta_text?: string
        cta_url?: string
        cta2_text?: string
        cta2_url?: string
        cta_inline_d?: boolean
        cta_inline_m?: boolean
        content_color?: string
        content_align?: 'left' | 'center' | 'right'
        class_name?: string
        custom_css?: string
    }
    moduleItems: Array<ReactNode>
}

const SWIPE_THRESHOLD = 50

const AnimatedSlide: React.FC<AnimatedSlideProps> = ({data, moduleItems}) => {
    const {
        title_animation_type,
        module_attached,
        svg,
        title,
        cta_text,
        cta_url,
        cta2_text,
        cta2_url,
        cta_inline_d,
        cta_inline_m,
        content_color,
        content_align,
        class_name,
        custom_css,
    } = data
    const {isDesktop} = useViewport()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(true)
    const {headerSize: headerSizeNotCollapsed} = useHeaderSize(false)

    const containerRef = useRef<HTMLDivElement>(null)
    const prevSlideIndex = useRef<number>(0)
    const swiperRef = useRef<SwiperType | null>(null)

    const styles = useMultiStyleConfig('AnimatedSlide', {headerSize, headerSizeNotCollapsed, accessibilityBannerSize}) as Record<string, any>

    const handleSlideChange = (swiper: SwiperType): void => {
        const lastSlideIndex = swiper.slides.length - 1

        // scroll on page top when scrolling up from last slide
        if (prevSlideIndex.current === lastSlideIndex) {
            window.scrollTo({top: 1, behavior: 'smooth'})
            // window.scrollTo({top: 0, behavior: 'smooth'})
        }

        // collapse menu when scrolling down from first slide
        if (prevSlideIndex.current === 0) {
            window.scrollTo({top: 1, behavior: 'instant'})
            // setExpanded(false)
        }

        // expand menu when scrolling up to first slide
        if (swiper.realIndex === 0) {
            window.scrollTo({top: 0, behavior: 'instant'})
            // setExpanded(true)
        }

        // unlock scroll at the end of the carousel
        if (swiper.realIndex === lastSlideIndex) {
            document.body.style.overflow = 'visible'
        }

        if (prevSlideIndex.current === lastSlideIndex) {
            document.body.style.overflow = 'hidden'
        }

        // adjust vertical position
        if (prevSlideIndex.current === 0) {
            containerRef.current?.classList.add('not-on-first-slide')
        }

        if (swiper.realIndex === 0) {
            containerRef.current?.classList.remove('not-on-first-slide')
        }

        // update ref
        prevSlideIndex.current = swiper.realIndex
    }

    // Mobile: custom touch handling to allow native scroll within slides
    // and trigger slide transitions only when at scroll boundaries
    useEffect(() => {
        if (isDesktop) return

        const container = containerRef.current
        if (!container) return

        let startY = 0
        let edgeStartY: number | null = null
        let edgeTriggered = false

        const handleTouchStart = (e: TouchEvent) => {
            startY = e.touches[0].clientY
            edgeStartY = null
            edgeTriggered = false
        }

        const handleTouchMove = (e: TouchEvent) => {
            if (!swiperRef.current || edgeTriggered) return

            const activeSlide = container.querySelector('.swiper-slide-active') as HTMLElement
            if (!activeSlide) return

            const currentY = e.touches[0].clientY
            const deltaY = startY - currentY // positive = finger moving up (scroll down intent)

            const {scrollTop, scrollHeight, clientHeight} = activeSlide
            const isScrollable = scrollHeight > clientHeight + 1

            if (!isScrollable) {
                // Slide content fits without scrolling — trigger slide change on swipe
                if (Math.abs(deltaY) > SWIPE_THRESHOLD) {
                    edgeTriggered = true
                    if (deltaY > 0) {
                        swiperRef.current.slideNext()
                    } else {
                        swiperRef.current.slidePrev()
                    }
                }
                return
            }

            const isAtTop = scrollTop <= 0
            const isAtBottom = Math.ceil(scrollTop) + clientHeight >= scrollHeight - 1

            if ((isAtBottom && deltaY > 0) || (isAtTop && deltaY < 0)) {
                // At edge and swiping past it — start tracking edge distance
                if (edgeStartY === null) {
                    edgeStartY = currentY
                }

                const edgeDelta = edgeStartY - currentY

                if (isAtBottom && edgeDelta > SWIPE_THRESHOLD) {
                    edgeTriggered = true
                    swiperRef.current.slideNext()
                } else if (isAtTop && edgeDelta < -SWIPE_THRESHOLD) {
                    edgeTriggered = true
                    swiperRef.current.slidePrev()
                }
            } else {
                // Not at edge or moving away from edge — reset
                edgeStartY = null
            }
        }

        container.addEventListener('touchstart', handleTouchStart, {passive: true})
        container.addEventListener('touchmove', handleTouchMove, {passive: true})

        return () => {
            container.removeEventListener('touchstart', handleTouchStart)
            container.removeEventListener('touchmove', handleTouchMove)
        }
    }, [isDesktop])

    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'instant'})
        document.body.style.overflow = 'hidden'
    }, [])

    const renderSwiper = () => (
        <Swiper
            direction={'vertical'}
            allowTouchMove={isDesktop}
            pagination={{
                enabled: isDesktop,
                clickable: true,
            }}
            mousewheel={{
                releaseOnEdges: isDesktop,
                enabled: isDesktop,
            }}
            keyboard
            speed={1000}
            modules={[Pagination, Mousewheel, Keyboard]}
            className="mySwiper"
            onSwiper={(swiper) => {
                swiperRef.current = swiper
            }}
            onRealIndexChange={(swiper) => {
                handleSlideChange(swiper)
            }}
        >
            {moduleItems?.map((component, idx) => (
                <SwiperSlide key={idx}>
                    <Box {...styles.moduleItemWrapper} data-testid="animated-slide-module-wrapper">
                        {component}
                    </Box>
                </SwiperSlide>
            ))}
        </Swiper>
    )

    return (
        <>
            <Box data-testid="animated-slide" sx={styles.container} ref={containerRef}>
                {title_animation_type === 'carousel' && renderSwiper()}
            </Box>
        </>
    )
}

export {AnimatedSlide}
